import React, { useEffect, useState } from "react";
import {
  Card,
  Col,
  DatePicker,
  Row,
  Select,
  Table,
  Popover,
  ConfigProvider,
} from "antd";
import { Tag, Button, Spin } from "antd";

import SummaryFooter from "./SummaryFooter";
import { ColumnGroupType, ColumnType } from "antd/es/table";
import { getAllTeachers, getMonthlyTeacherReport } from "../../../api/teachers";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";

import dayjs from "dayjs";
import "dayjs/locale/fr";
import "antd/dist/reset.css";
import frFR from "antd/locale/fr_FR";
import { toast } from "sonner";
import { FilePdfOutlined } from "@ant-design/icons";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import { getSessionsByYear } from "../../../api/settingEDT";
import ReportCourseBubble from "./ReportCourseBubble";
import MonthlyReportApercuModal from "./monthlyReportApercu_modal";
import InfosAlertYearDisabled from "../../../core/common/alerts/infosAlertYearDisabled";

const { Option } = Select;

dayjs.locale("fr");

const MonthlyReport = () => {
  const { selectedYear } = useAcademicYear();
  const [selectedTeacher, setSelectedTeacher] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<any>();
  const [allSessions, setAllSessions] = useState<any[]>([]);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any[]>([]);
  const [popoverVisible, setPopoverVisible] = useState<any>({});
  const [hourlyRate, setHourlyRate] = useState<number | null>(null);
  const [teachers, setTeachers] = useState<any[]>([]);

  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Fonction pour récupérer tous les professeurs de la base de données
  const fetchTeachers = async () => {
    try {
      const response = await getAllTeachers(
        buildPermissionContext(Resource.TEACHER_REPORT, Action.READ),
      );

      if (response.data.length !== 0) {
        setSelectedTeacher(response.data[0]?.id);
      }

      setTeachers(response.data || []);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Erreur lors de la récupération des enseignants",
      );
    }
  };

  const fetchAllSessions = async () => {
    try {
      const response = await getSessionsByYear(
        selectedYear?.id,
        buildPermissionContext(Resource.TEACHER_REPORT, Action.READ),
      );
      setAllSessions(response.data || []);
    } catch (error: any) {
      // console.log("Erreur lors de la récupération des sessions:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur est survenue, veuillez réessayer plus tard.",
      );
    }
  };

  // Fonction pour charger les infos du rapport mensuel
  const loadData = async () => {
    try {
      // Build a complete month grid: every day gets an entry, sessions are slotted into their day
      const year = new Date(selectedMonth || new Date()).getFullYear();
      const month = new Date(selectedMonth || 0).getMonth(); // January

      const response = await getMonthlyTeacherReport(
        selectedTeacher,
        new Date(selectedMonth || new Date()).getMonth() + 1,
        selectedYear?.id,
        // buildPermissionContext(Resource.TEACHER_REPORT, Action.READ)
      );
      // console.log("result : ", result);
      // console.log("response.data?.courses : ", response.data?.courses);
      setHourlyRate(response.data?.hourlyRate);

      const formattedResponse = response.data?.courses?.flatMap((item: any) =>
        item.CourseClasses.flatMap((c: any) =>
          c.CourseVersionEDTs.map((edt: any) => ({
            ...edt,
            Subject: item.Subject,
            Classes: item.Classes,
            CourseClasses: item.CourseClasses,
          })),
        ),
      );

      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const fullMonth: any[] = [];
      for (let i = 0; i < daysInMonth; i++) {
        const date = new Date(year, month, i + 1);
        const dayLabel = date.toLocaleDateString("fr-FR", {
          weekday: "short",
          day: "numeric",
          month: "numeric",
          year: "numeric",
        });
        // Trouver tous les cours de ce jour
        // Dédoublonner les cours par leur id unique
        const coursesOfDay = formattedResponse
          .filter((d: any) => d.date_course === date.toISOString().slice(0, 10))
          .filter(
            (course: any, index: any, self: any) =>
              index ===
              self.findIndex(
                (c: any) =>
                  c.id_sessionStart === course.id_sessionStart &&
                  c.id_sessionEnd === course.id_sessionEnd &&
                  c.id_classroom === course.id_classroom &&
                  c.state_course === course.state_course &&
                  c.type_course === course.type_course &&
                  c.day_course === course.day_course &&
                  c.date_course === course.date_course &&
                  c.Subject.id === course.Subject.id,
              ),
          );
        // si aucun cours, passer au jour suivant
        if (coursesOfDay.length === 0) continue;

        const totalSessions = coursesOfDay.length;
        const totalAbsences = coursesOfDay.filter(
          (c: any) => c.state_course === "Annulé",
        ).length;
        const completedSessions = totalSessions - totalAbsences;

        const totalHours = coursesOfDay.reduce((sum: any, c: any) => {
          if (!c.SessionStart?.start_time || !c.SessionEnd?.end_time)
            return sum;
          const [startH, startM] = c.SessionStart.start_time
            .split(":")
            .map(Number);
          const [endH, endM] = c.SessionEnd.end_time.split(":").map(Number);
          const minutes = endH * 60 + endM - (startH * 60 + startM);
          return sum + (minutes < 0 ? minutes + 24 * 60 : minutes); // handle overnight
        }, 0);

        const delay = coursesOfDay.reduce((sum: any, c: any) => {
          if (!c?.AbsenceTeachers?.[0]?.time || c.state_course === "Annulé")
            return sum;
          const [h, m] = c.AbsenceTeachers[0].time.split(":").map(Number);
          return sum + (h * 60 + m);
        }, 0);
        const completedHours = coursesOfDay.reduce((sum: any, c: any) => {
          if (
            !c.SessionStart?.start_time ||
            !c.SessionEnd?.end_time ||
            c.state_course === "Annulé"
          )
            return sum;
          const [startH, startM] = c.SessionStart.start_time
            .split(":")
            .map(Number);
          const [endH, endM] = c.SessionEnd.end_time.split(":").map(Number);
          const minutes = endH * 60 + endM - (startH * 60 + startM);
          return sum + (minutes < 0 ? minutes + 24 * 60 : minutes); // handle overnight
        }, 0);

        fullMonth.push({
          date: date.toISOString().slice(0, 10),
          day: dayLabel,
          sessions: coursesOfDay.map((c: any) => {
            const [h, m] = c?.AbsenceTeachers?.[0]?.time
              ?.split(":")
              .map(Number) || [0, 0];
            // console.log("Absence Type: ",c)
            return {
              id_sessionStart: c.id_sessionStart,
              id_sessionEnd: c.id_sessionEnd,
              saved: true,
              cours: {
                ...c.Subject,
                Classes: c.Classes,
                CourseClasses: c.CourseClasses,
                id_classroom: c.id_classroom,
                state_course: c.state_course,
                type_course: c.type_course,
                reason_cancel: c.reason_cancel,
                reason_delay: c?.AbsenceTeachers[0]?.reason,
                type_delay: c?.AbsenceTeachers[0]?.type,
                courseDelay: h * 60 + m || 0,
                date_course: c.date_course,
                id_sessionStart: c.id_sessionStart,
                id_sessionEnd: c.id_sessionEnd,
              },
            };
          }),
          totalSessions,
          totalAbsences,
          completedSessions,
          completedHoursWithDelay: completedHours - delay,
          totalHours,
          delay,
          completedHours,
        });
      }

      setData(fullMonth);
    } catch (error: any) {
      // console.log(
      //   "Erreur lors du chargement du rapport mensuel des professeurs: ",
      //   error
      // );
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Erreur lors du chargement du rapport mensuel des professeurs",
      );
    } finally {
      setLoading(false);
    }
  };

  // Effect pour charger les enseignants lorsque le composant monte
  useEffect(() => {
    fetchTeachers();
    fetchAllSessions();
  }, []);

  // Effect pour charger les données du rapport mensuel lorsque l'enseignant ou le mois change
  useEffect(() => {
    if (selectedTeacher && selectedMonth) {
      setLoading(true);
      loadData();
    }
  }, [selectedTeacher, selectedMonth]);

  const handleCourseClick = (dayIndex: number, sessionIndex: number) => {
    setPopoverVisible({ [`${dayIndex}-${sessionIndex}`]: true });
  };

  const handleTeacherChange = (value: string) => {
    setSelectedTeacher(value);
  };

  const handleMonthChange = (date: any, dateString: string) => {
    setSelectedMonth(dateString);
  };

  const columns = [
    {
      title: "Jour",
      dataIndex: "day",
      key: "day",
      width: 150,
      onHeaderCell: () => ({
        style: { backgroundColor: "#d6e4ff", border: "1px solid #000" },
      }),
      onCell: () => ({ style: { border: "1px solid #000" } }),
    },
    {
      title: "Séances",
      onHeaderCell: () => ({
        style: { backgroundColor: "#d6e4ff", border: "1px solid #000" },
      }),
      children: allSessions.map((session: any, colIdx) => ({
        onHeaderCell: () => ({
          style: { backgroundColor: "#d6e4ff", border: "1px solid #000" },
        }),
        title: (
          <div style={{ textAlign: "center" }}>
            <div>{session.name}</div>
            <div style={{ fontSize: "0.75em", color: "#888" }}>
              {session.start_time?.slice(0, 5)} -{" "}
              {session.end_time?.slice(0, 5)}
            </div>
          </div>
        ),
        key: `session${session.id}`,
        width: 120,
        onCell: () => ({ style: { border: "1px solid #000" } }),
        render: (_: any, record: any, dayIndex: number) => {
          const sessions = record?.sessions || [];
          // Trouver la séance qui commence à cette colonne
          const sessionAtCol = sessions.find(
            (s: any) =>
              allSessions.findIndex((ss) => ss.id === s.id_sessionStart) ===
              colIdx,
          );

          // Trouver si cette cellule est "couverte" par une fusion déjà commencée
          const insideMerged = sessions.find((s: any) => {
            const start = allSessions.findIndex(
              (ss) => ss.id === s.id_sessionStart,
            );
            const end = allSessions.findIndex(
              (ss) => ss.id === s.id_sessionEnd,
            );
            return colIdx > start && colIdx <= end;
          });

          if (insideMerged) {
            return { props: { colSpan: 0 } }; // masquer les cellules fusionnées
          }

          if (!sessionAtCol) {
            return {
              children: null,
              props: { style: { border: "1px solid #000" } },
            }; // rien ici
          }
          const c = sessionAtCol.cours;
          const startIndex = allSessions.findIndex(
            (s) => s.id === sessionAtCol.id_sessionStart,
          );
          const endIndex = allSessions.findIndex(
            (s) => s.id === sessionAtCol.id_sessionEnd,
          );

          const colorState = c.state_course === "Annulé" ? "gold" : "green";

          const colorType = c.type_course === "Normal" ? "geekblue" : "volcano";
          // console.log("popover: ", c);

          return {
            props: {
              colSpan: endIndex - startIndex + 1,
              style: {
                border: "1px solid #000",
                backgroundColor:
                  c.state_course === "Annulé" ? "rgba(216, 27, 27, 0.4)" : "",
              },
            },
            children: (
              <Popover
                content={
                  <ReportCourseBubble
                    course={c}
                    open={popoverVisible}
                    dayIndex={dayIndex}
                    sessionIndex={colIdx}
                    onSave={() => loadData()}
                    onCancel={() =>
                      setPopoverVisible({ [`${dayIndex}-${colIdx}`]: false })
                    }
                  />
                }
                title="Informations du cours"
                trigger="click"
                open={popoverVisible[`${dayIndex}-${colIdx}`] || false}
                onOpenChange={(visible) =>
                  setPopoverVisible({ [`${dayIndex}-${colIdx}`]: visible })
                }
              >
                <div
                  key={c.id}
                  className="p-1 mb-1 rounded"
                  style={{
                    position: "relative",
                    cursor: "pointer",
                    textAlign: "center",
                  }}
                  onClick={() => handleCourseClick(dayIndex, colIdx)}
                >
                  <strong>{c?.label}</strong>
                  <br />
                  <small>
                    {allSessions
                      .find((s) => s.id === sessionAtCol.id_sessionStart)
                      ?.start_time?.slice(0, 5)}{" "}
                    -{" "}
                    {allSessions
                      .find((s) => s.id === sessionAtCol.id_sessionEnd)
                      ?.end_time?.slice(0, 5)}
                  </small>
                  <br />
                  <Tag color={colorState}>{c.state_course}</Tag>
                  <Tag color={colorType}>{c.type_course}</Tag>
                  {c?.courseDelay &&
                  c?.courseDelay !== 0 &&
                  c.state_course !== "Annulé" ? (
                    <Tag color={"orange"}>Retard</Tag>
                  ) : null}
                  {c.type_delay === "Abscence" &&
                    c.state_course === "Annulé" && (
                      <Tag color={"orange"}>Absence</Tag>
                    )}
                </div>
              </Popover>
            ),
          };
        },
      })),
    },
    {
      title: "Charge",
      onHeaderCell: () => ({
        style: { backgroundColor: "#d6e4ff", border: "1px solid #000" },
      }),
      children: [
        {
          title: "Nb Séances",
          dataIndex: "totalSessions",
          key: "totalSessions",
          align: "center",
          width: 80,
          onHeaderCell: () => ({
            style: { backgroundColor: "#d6e4ff", border: "1px solid #000" },
          }),
          onCell: () => ({ style: { border: "1px solid #000" } }),
        },
        {
          title: "Charge (h)",
          dataIndex: "totalHours",
          key: "totalHours",
          align: "center",
          width: 100,
          onHeaderCell: () => ({
            style: { backgroundColor: "#d6e4ff", border: "1px solid #000" },
          }),
          onCell: () => ({ style: { border: "1px solid #000" } }),
          render: (minutes: number) => {
            const h = Math.floor(minutes / 60);
            const m = minutes % 60;
            return `${h}h ${m.toString().padStart(2, "0")}`;
          },
        },
        {
          title: "Absences",
          dataIndex: "totalAbsences",
          key: "totalAbsences",
          align: "center",
          width: 80,
          onHeaderCell: () => ({
            style: {
              backgroundColor: "rgba(255, 0, 0, 0.2)",
              border: "1px solid #000",
            },
          }),
          onCell: () => ({ style: { border: "1px solid #000" } }),
          render: (value: number) => (
            <div
              style={{
                color: value !== 0 ? "rgba(255, 0, 0)" : undefined,
                fontWeight: value !== 0 ? "bold" : undefined,
              }}
            >
              {value}
            </div>
          ),
        },
        {
          title: "Séances F.",
          dataIndex: "completedSessions",
          key: "completedSessions",
          align: "center",
          width: 80,
          onHeaderCell: () => ({
            style: {
              backgroundColor: "rgba(0, 255, 0, 0.2)",
              border: "1px solid #000",
            },
          }),
          onCell: () => ({ style: { border: "1px solid #000" } }),
          render: (value: number) => (
            <div
              style={{
                color: value !== 0 ? "rgba(27, 171, 27, 1)" : undefined,
                fontWeight: value !== 0 ? "bold" : undefined,
              }}
            >
              {value}
            </div>
          ),
        },
        {
          title: "Retard (h)",
          dataIndex: "delay",
          key: "delay",
          align: "center",
          width: 100,
          onHeaderCell: () => ({
            style: {
              backgroundColor: "rgba(255, 255, 0, 0.2)",
              border: "1px solid #000",
            },
          }),
          onCell: () => ({ style: { border: "1px solid #000" } }),
          render: (minutes: number) => {
            const h = Math.floor(minutes / 60);
            const m = minutes % 60;
            return (
              <div
                style={{
                  color: minutes !== 0 ? "rgba(183, 183, 12, 1)" : undefined,
                  fontWeight: minutes !== 0 ? "bold" : undefined,
                }}
              >
                {`${h}h ${m.toString().padStart(2, "0")}`}
              </div>
            );
          },
        },
        {
          title: "Heures F. (h)",
          dataIndex: "completedHoursWithDelay",
          key: "completedHoursWithDelay",
          align: "center",
          width: 100,
          onHeaderCell: () => ({
            style: {
              backgroundColor: "rgba(0, 0, 255, 0.2)",
              border: "1px solid #000",
            },
          }),
          onCell: () => ({ style: { border: "1px solid #000" } }),
          render: (minutes: number) => {
            const h = Math.floor(minutes / 60);
            const m = minutes % 60;
            return (
              <div
                style={{
                  color: minutes !== 0 ? "rgba(31, 31, 150, 1)" : undefined,
                  fontWeight: minutes !== 0 ? "bold" : undefined,
                }}
              >{`${h}h ${m.toString().padStart(2, "0")}`}</div>
            );
          },
        },
      ],
    },
  ];

  useEffect(() => {
    if (!selectedYear || selectedMonth) return;

    const today = dayjs();

    const defaultMonth = today;

    handleMonthChange(defaultMonth, defaultMonth.format("YYYY-MM"));
  }, [selectedYear, selectedMonth]);

  return (
    <>
      <div className="page-wrapper">
        <div
          className="content container-fluid d-flex flex-column"
          style={{ height: "calc(100vh - 60px)" }}
        >
          <div
            className="sticky-top bg-white flex-shrink-0"
            style={{ zIndex: 820, top: 0 }}
          >
            <Card
              style={{
                marginTop: "24px",
                background: "#ffffff",
                border: "1px solid #d9d9d9",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              <Row
                gutter={[16, 16]}
                align="stretch"
                className="d-flex align-items-center gap-2"
              >
                <Col
                  xs={24}
                  sm={12}
                  md={5}
                  className="d-flex align-items-center gap-2"
                  style={{
                    background: "#d6e4ff",
                    padding: "12px",
                    borderRadius: "6px",
                    minWidth: 0, // prevents overflow
                  }}
                >
                  <i className="typcn typcn-mortar-board text-dark fs-1 flex-shrink-0"></i>
                  <Select
                    showSearch
                    style={{ width: "100%", minWidth: 0 }}
                    placeholder="Rechercher et sélectionner un enseignant"
                    value={selectedTeacher || undefined}
                    onChange={handleTeacherChange}
                    filterOption={(input, option) =>
                      (option?.children as unknown as string)
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    popupMatchSelectWidth={false} // dropdown adapts to content
                  >
                    {teachers.map((teacher) => (
                      <Option key={teacher.id} value={teacher.id}>
                        {`${teacher.firstname} ${teacher.lastname}`}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col
                  xs={24}
                  sm={12}
                  md={4}
                  className="d-flex align-items-center justify-content-between gap-2"
                  style={{
                    background: "#d6e4ff",
                    padding: "12px",
                    borderRadius: "6px",
                  }}
                >
                  <i className="fa fa-calendar  text-dark fs-2 py-3"></i>
                  <ConfigProvider locale={frFR}>
                    <DatePicker
                      picker="month"
                      style={{ width: "100%" }}
                      format={(value) =>
                        value
                          ? value
                              .format("MMMM YYYY")
                              .replace(/^\w/, (c) => c.toUpperCase())
                          : ""
                      }
                      value={
                        selectedMonth ? dayjs(selectedMonth, "YYYY-MM") : null
                      }
                      onChange={(date) => {
                        if (!date) return;
                        handleMonthChange(date, date.format("YYYY-MM"));
                      }}
                      placeholder="Sélectionner le mois"
                    />
                  </ConfigProvider>
                </Col>
                <Col
                  xs={24}
                  sm={12}
                  md={4}
                  style={{
                    height: "100%",
                  }}
                >
                  <Button
                    icon={<FilePdfOutlined style={{ fontSize: "32px" }} />}
                    onClick={() => setShowPreviewModal(true)}
                    disabled={data.length === 0}
                    className="d-flex flex-column align-items-center justify-content-center"
                    style={{
                      width: "100%",
                      padding: "12px",
                      height: "100%",
                      background: "#ffe6d1",
                      borderColor: "#ff7c00",
                      color: "#b35900",
                      whiteSpace: "normal",
                      textAlign: "center",
                      lineHeight: 1.2,
                    }}
                  >
                    <span style={{ fontSize: "0.9rem", marginTop: "4px" }}>
                      Exporter en PDF
                    </span>
                  </Button>
                </Col>
              </Row>
            </Card>
            {!selectedYear?.statut && <InfosAlertYearDisabled />}

            <h4 className="mt-3">
              Rapport Mensuel —{" "}
              {selectedMonth
                ? new Date(selectedMonth)
                    .toLocaleString("fr-FR", { month: "long", year: "numeric" })
                    .replace(/^\w/, (c) => c.toUpperCase())
                : "Sélectionner un mois valide"}
            </h4>
          </div>
          <div
            className="flex-grow-1"
            style={{ minHeight: 0, overflow: "auto", paddingBottom: "1rem" }}
          >
            <div className="">
              {!selectedMonth || !selectedTeacher ? (
                <div className="text-center py-5">
                  <h5 className="text-muted">
                    Veuillez sélectionner un mois et un enseignant valides.
                  </h5>
                </div>
              ) : loading ? (
                <div className="text-center py-5">
                  <Spin size="large" />
                </div>
              ) : data.length === 0 ? (
                <div className="text-center py-5">
                  <h5 className="text-muted">
                    Aucune donnée disponible pour cet enseignant et ce mois.
                  </h5>
                </div>
              ) : (
                <>
                  <Table
                    dataSource={data}
                    columns={
                      columns as (ColumnGroupType<any> | ColumnType<any>)[]
                    }
                    pagination={false}
                    rowKey="date"
                    bordered
                    size="small"
                    scroll={{ x: "max-content" }}
                    className="black-grid shadow-sm"
                  />
                  <SummaryFooter data={data} hourlyRate={hourlyRate} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <MonthlyReportApercuModal
        show={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        metaData={{
          id_teacherProfil: selectedTeacher,
          Teacher: {
            firstname:
              teachers.find((t: any) => t.id === selectedTeacher)?.firstname ||
              "",
            lastname:
              teachers.find((t: any) => t.id === selectedTeacher)?.lastname ||
              "",
          },
          month: selectedMonth,
          academicYear: selectedYear,
        }}
      />
    </>
  );
};

export default MonthlyReport;
